import { useAuth } from '@/contexts/auth-context';
import { Link, useRouterState } from '@tanstack/react-router';
import { LogOut, MoveUpRight, Settings, CreditCard, FileText, LucideIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface NavItem {
  label: string;
  value?: any;
  href: string;
  icon: LucideIcon;
  external?: boolean;
}

interface Profile01Props {
  name: string;
  role?: string;
  avatar?: string;
  subscription?: string;
}

const defaultProfile = {
  name: 'User',
  role: 'Member',
  avatar: undefined,
  subscription: 'Free Trial',
} satisfies Required<Profile01Props>;

export default function Profile01({
  name = defaultProfile.name,
  role = defaultProfile.role,
  avatar = defaultProfile.avatar,
  subscription = defaultProfile.subscription,
}: Partial<Profile01Props> = defaultProfile) {
  const { logout } = useAuth();

  // Generate initials from user name or email
  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    // For email or single name, take first 1-2 characters
    return name.slice(0, 2).toUpperCase();
  };

  const navItems: NavItem[] = [
    // {
    //   label: 'Subscription',
    //   value: subscription,
    //   href: '#',
    //   icon: CreditCard,
    //   external: false,
    // },
    {
      label: 'Settings',
      href: '/dashboard/settings',
      icon: Settings,
    },
    // {
    //   label: 'Terms & Policies',
    //   href: '#',
    //   icon: FileText,
    //   external: true,
    // },
  ];
  const router = useRouterState();

  function NavItem({ href, icon: Icon, children, external }: { href: string; icon: LucideIcon; children: React.ReactNode; external?: boolean }) {
    const isActive = router.location.pathname === href || (href !== '#' && router.location.pathname.startsWith(href));
    const className = `flex items-center px-3 py-2 text-sm rounded-md font-medium transition-colors
      ${isActive ? 'text-primary bg-primary/10 dark:bg-primary/20' : 'text-gray-700 dark:text-gray-200'}
      hover:text-primary dark:hover:text-primary hover:bg-[#23232a] dark:hover:bg-[#23232a]'`;

    if (external) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
          <Icon className={`h-4 w-4 mr-3 flex-shrink-0 opacity-80 ${isActive ? 'text-primary' : ''}`} />
          {children}
        </a>
      );
    }

    return (
      <Link to={href} className={className} aria-current={isActive ? 'page' : undefined}>
        <Icon className={`h-4 w-4 mr-3 flex-shrink-0 opacity-80 ${isActive ? 'text-primary' : ''}`} />
        {children}
        {isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary shadow-sm" />}
      </Link>
    );
  }
  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="relative overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800">
        <div className="relative px-6 pt-12 pb-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="relative shrink-0">
              <Avatar className="h-[72px] w-[72px] ring-4 ring-white dark:ring-zinc-900">
                {avatar && <AvatarImage src={avatar} alt={name} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-xl font-medium">
                  {getInitials(name)}
                </AvatarFallback>
              </Avatar>
              <div className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">{name}</h2>
              <p className="text-zinc-600 dark:text-zinc-400">{role}</p>
            </div>
          </div>
          <div className="h-px bg-zinc-200 dark:bg-zinc-800 my-6" />
          <div className="space-y-2">
            {navItems.map((item) => (
              <NavItem key={item.label} href={item.href} icon={item.icon}>
                {item.label}
              </NavItem>
            ))}

            <button
              onClick={() => logout('/')}
              type="button"
              className="w-full flex items-center justify-between p-2 
                                hover:bg-[#23232a] dark:hover:bg-[#23232a] 
                                rounded-lg transition-colors duration-200"
            >
              <div className="flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Logout</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
